import React, { useState } from "react";
import {
  Typography,
  Box,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - i);

function buildCsv(rows) {
  const headers = [
    "Date Created",
    "First Name",
    "Last Name",
    "Appointment Type",
    "Provider Order ID",
    "Pharmacy",
    "Status",
    "Invoice Number",
    "Paid Amount",
  ];
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.dateCreated,
        r.firstName,
        r.lastName,
        r.appointmentType,
        r.providerOrderId,
        r.pharmacy,
        r.status,
        r.invoiceNumber,
        r.paidAmount,
      ]
        .map(escape)
        .join(",")
    ),
  ];
  return lines.join("\r\n");
}

function downloadCsv(csv, month, year) {
  const monthName = MONTHS[month - 1];
  const filename = `HubMed_Appointments_${monthName}_${year}.csv`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const HubMedReportsPage = () => {
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await phappsRequest(
        API_PATHS.HUBMED_ADMIN_APPOINTMENT_REPORT,
        "GET",
        null,
        { month, year }
      );
      if (!data?.rows?.length) {
        setError("No appointments found for the selected month.");
        return;
      }
      const csv = buildCsv(data.rows);
      downloadCsv(csv, month, year);
    } catch (err) {
      setError(err?.message ?? "Failed to download report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600 }}>
      <Typography variant="h5" fontWeight="bold" mb={1}>
        Appointment Report
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Download a CSV of all HubMed appointments for a given month.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", flexWrap: "wrap", mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Month</InputLabel>
          <Select
            value={month}
            label="Month"
            onChange={(e) => setMonth(e.target.value)}
          >
            {MONTHS.map((name, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={year}
            label="Year"
            onChange={(e) => setYear(e.target.value)}
          >
            {YEAR_OPTIONS.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownload}
          disabled={loading}
        >
          {loading ? "Downloading…" : "Download Appointment Report"}
        </Button>
      </Box>

      {error && (
        <Alert severity={error.startsWith("No appointments") ? "info" : "error"}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default HubMedReportsPage;
